/** Thin client for Rally's demo and guest APIs (the MCP traffic goes through host.ts). */
import { srv } from './server.ts';

async function json<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
  return body;
}
/** This browser's sandbox key: the demo API and MCP calls for its household must carry it. */
let demoKey = '';
export const setDemoKey = (key: string) => void (demoKey = key);
export const demoHeaders = (): Record<string, string> => (demoKey ? { 'x-demo-key': demoKey } : {});

const post = <T>(url: string, body: unknown = {}) =>
  fetch(srv(url), { method: 'POST', headers: { 'content-type': 'application/json', ...demoHeaders() }, body: JSON.stringify(body) }).then((r) => json<T>(r));

export type DemoPlan = {
  id: string;
  title: string;
  status: string;
  card: any;
  options: Array<{ id: string; start: string; end: string; label: string }>;
  chosenOptionId: string | null;
  events: Array<{ at: string; actor: string; kind: string; text: string }>;
  outbox: Array<{ id: string; at: string; to: string; toPersonId: string; kind: string; text: string; link?: string }>;
  guests: Array<{ personId: string; name: string; status: string; token: string | null }>;
  organizer: { id: string; name: string; token: string | null };
};

export type MemoryFact = {
  id: string;
  said: string;
  predicate: string;
  source: 'organizer' | 'self' | 'debrief';
  plans: number;
  validFrom: string;
  validTo: string | null;
  recordedAt: string;
  retiredAt: string | null;
  hiddenFrom: string[];
  hiddenUntil: string | null;
};

export type DemoState = {
  team: Array<{ id: string; name: string; watches: string; writes: string }>;
  memory: { episodes: Array<{ id: string; title: string; start: string; source: string; came: number }>; facts: MemoryFact[] };
  householdId: string;
  now: string;
  clockOffsetHours: number;
  timezone: string;
  people: Array<{ id: string; name: string; relation: string | null; householdMember: boolean }>;
  plans: DemoPlan[];
};

export type OrganizerView = {
  planId: string;
  title: string;
  status: string;
  organizer: string;
  surpriseFor: string[];
  hostDeposit: { status: 'due' | 'paid'; amountCents: number; itemId: string } | null;
};

export type GuestView = {
  planId: string;
  title: string;
  status: string;
  organizer: string;
  surpriseFor: string[];
  timezone: string;
  options: Array<{ id: string; start: string; label: string; spoken: string }>;
  you: {
    id: string;
    name: string;
    status: string;
    canMake: string[];
    note?: string;
    needs: { diets: string[]; allergies: string[]; stepFree: boolean; budgetPerHeadCents: number | null; setBySelf: boolean };
    deposit: { status: 'due' | 'paid'; amountCents: number } | null;
    depositItemId: string | null;
  };
  coming: string[];
  venue: { name: string; address: string; neighborhood: string; when: string; code: string; room: string } | null;
};

export const api = {
  config: () => fetch(srv('/api/config')).then((r) => json<{ llm: string | null; storage: string; serverless: boolean; version: string }>(r)),
  newDemo: () => post<{ householdId: string; key: string }>('/api/demo/session'),
  state: (hh: string) => fetch(srv(`/api/demo/${hh}/state`), { headers: demoHeaders() }).then((r) => json<DemoState>(r)),
  advance: (hh: string, hours: number) => post<{ now: string }>(`/api/demo/${hh}/advance`, { hours }),
  simulate: (hh: string, live: string[]) => post<{ answered: string[] }>(`/api/demo/${hh}/simulate`, { live }),
  reset: (hh: string) => post(`/api/demo/${hh}/reset`),
  skill: () => fetch(srv('/skill.md')).then((r) => (r.ok ? r.text() : '')),
  guest: (token: string) => fetch(srv(`/api/guest/${token}`)).then((r) => json<GuestView>(r)),
  organizer: (token: string) => fetch(srv(`/api/organizer/${token}`)).then((r) => json<OrganizerView>(r)),
  answer: (token: string, body: { status: 'in' | 'out' | 'maybe'; canMake: string[]; note?: string }) => post<GuestView>(`/api/guest/${token}/answer`, body),
  needs: (token: string, body: { diets: string[]; allergies: string[]; stepFree: boolean; budgetPerHeadCents: number | null }) => post<GuestView>(`/api/guest/${token}/needs`, body),
  /**
   * Open a UCP checkout session for one deposit, the way Alexa+ does before it asks for the voice code.
   * A sandbox's checkouts need its key, or the payer's own private link (`link`).
   */
  async openCheckout(itemId: string, buyer: string, link?: string): Promise<Checkout | { error: string }> {
    const create = await fetch(srv('/ucp/checkout-sessions'), { method: 'POST', headers: ucpHeaders(link), body: JSON.stringify({ line_items: [{ item: { id: itemId }, quantity: 1 }], buyer: { first_name: buyer } }) })
      .then((r) => r.json())
      .catch(() => ({}));
    if (!create.id) return { error: create.messages?.[0]?.content ?? 'Could not start checkout' };
    const total = (create.totals as Array<{ type: string; amount: number }> | undefined)?.find((t) => t.type === 'total')?.amount ?? 0;
    return { id: create.id, totalCents: total };
  },
  /** Complete an open checkout with an Amazon Pay network token. */
  async completeCheckout(id: string, link?: string): Promise<{ status: string; confirmation?: string; error?: string }> {
    const done = await fetch(srv(`/ucp/checkout-sessions/${id}/complete`), {
      method: 'POST',
      headers: ucpHeaders(link),
      body: JSON.stringify({ payment: { instruments: [{ handler_id: 'amazon_pay_network_token', type: 'card', credential: { type: 'network_token', encrypted_token: `tok_${crypto.randomUUID()}` } }] } }),
    })
      .then((r) => r.json())
      .catch(() => ({}));
    return done.status === 'completed' ? { status: 'completed', confirmation: done.order?.id } : { status: 'error', error: done.messages?.[0]?.content ?? 'Payment failed' };
  },
  /** Cancel an open checkout: nothing is charged. */
  cancelCheckout: (id: string, link?: string) => fetch(srv(`/ucp/checkout-sessions/${id}/cancel`), { method: 'POST', headers: ucpHeaders(link) }).then(() => undefined, () => undefined),
  /** Pay a deposit the way Alexa+ does: open a UCP checkout session, then complete it with an Amazon Pay network token. */
  async payDeposit(itemId: string, buyer: string, link?: string): Promise<{ status: string; confirmation?: string; error?: string }> {
    const checkout = await api.openCheckout(itemId, buyer, link);
    if ('error' in checkout) return { status: 'error', error: checkout.error };
    return api.completeCheckout(checkout.id, link);
  },
};

export type Checkout = { id: string; totalCents: number };

const ucpHeaders = (link?: string) => ({
  'content-type': 'application/json',
  'UCP-Agent': 'profile="https://alexa.amazon.com/.well-known/ucp"',
  'Idempotency-Key': crypto.randomUUID(),
  ...demoHeaders(),
  ...(link ? { 'x-rally-link': link } : {}),
});
